/**
 * Cognito Authentication Client for EASI 2.0
 *
 * Provides authentication flows using AWS Amplify Auth v6:
 * - Sign in / Sign out / Sign up
 * - MFA support (required for clinicians per HIPAA)
 * - Token refresh handling
 * - Session persistence
 *
 * HIPAA Requirements:
 * - 164.312(d): Person or entity authentication
 * - 164.312(a)(2)(i): Unique user identification
 */

import {
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  setUpTOTP,
  verifyTOTPSetup,
  confirmSignIn,
  fetchUserAttributes,
  type SignInInput,
  type SignUpInput,
  type ConfirmSignUpInput,
  type ResetPasswordInput,
  type ConfirmResetPasswordInput,
  type UpdatePasswordInput,
} from '@aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { amplifyConfig, config } from '../config/environment';

// Initialize Amplify
Amplify.configure(amplifyConfig);

// Cognito hosted UI domain for SSO token refresh
const COGNITO_DOMAIN = 'auth.easievaluation.com';

/**
 * Authentication result types
 */
export interface AuthUser {
  userId: string;
  username: string;
  email: string;
  emailVerified: boolean;
  mfaEnabled: boolean;
  roles: string[];
  organizationId?: string;
}

export interface AuthSession {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  expiresAt: Date;
}

export interface AuthResult {
  success: boolean;
  user?: AuthUser;
  session?: AuthSession;
  nextStep?: string;
  error?: string;
}

export interface MFASetupResult {
  secretCode: string;
  qrCodeUri: string;
}

/**
 * Sign in a user with username/email and password
 */
export async function signInUser(
  username: string,
  password: string
): Promise<AuthResult> {
  try {
    const input: SignInInput = {
      username,
      password,
    };

    const result = await signIn(input);

    if (result.isSignedIn) {
      const user = await getAuthenticatedUser();
      const session = await getAuthSession();
      return { success: true, user: user || undefined, session: session || undefined };
    }

    // Handle MFA or other challenges
    const nextStep = result.nextStep?.signInStep;

    if (nextStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
      return { success: false, nextStep: 'MFA_TOTP_REQUIRED' };
    }

    if (nextStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
      return { success: false, nextStep: 'MFA_SMS_REQUIRED' };
    }

    if (nextStep === 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION') {
      return { success: false, nextStep: 'MFA_SETUP_REQUIRED' };
    }

    if (nextStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { success: false, nextStep: 'NEW_PASSWORD_REQUIRED' };
    }

    return { success: false, nextStep };
  } catch (error: any) {
    console.error('Sign in error:', error.name);
    return {
      success: false,
      error: getAuthErrorMessage(error),
    };
  }
}

/**
 * Complete MFA verification during sign in
 */
export async function confirmMFA(code: string): Promise<AuthResult> {
  try {
    const result = await confirmSignIn({ challengeResponse: code });

    if (result.isSignedIn) {
      const user = await getAuthenticatedUser();
      const session = await getAuthSession();
      return { success: true, user: user || undefined, session: session || undefined };
    }

    return { success: false, nextStep: result.nextStep?.signInStep };
  } catch (error: any) {
    return { success: false, error: getAuthErrorMessage(error) };
  }
}

/**
 * Sign out the current user
 */
export async function signOutUser(global: boolean = false): Promise<void> {
  try {
    await signOut({ global });
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
}

/**
 * Register a new user
 */
export async function signUpUser(
  username: string,
  password: string,
  email: string,
  attributes?: Record<string, string>
): Promise<AuthResult> {
  try {
    const input: SignUpInput = {
      username,
      password,
      options: {
        userAttributes: {
          email,
          ...attributes,
        },
      },
    };

    const result = await signUp(input);

    if (result.isSignUpComplete) {
      return { success: true };
    }

    return {
      success: false,
      nextStep: 'CONFIRM_SIGN_UP',
    };
  } catch (error: any) {
    return { success: false, error: getAuthErrorMessage(error) };
  }
}

/**
 * Confirm user registration with verification code
 */
export async function confirmUserSignUp(
  username: string,
  code: string
): Promise<AuthResult> {
  try {
    const input: ConfirmSignUpInput = {
      username,
      confirmationCode: code,
    };

    const result = await confirmSignUp(input);

    if (result.isSignUpComplete) {
      return { success: true };
    }

    return { success: false, nextStep: result.nextStep?.signUpStep };
  } catch (error: any) {
    return { success: false, error: getAuthErrorMessage(error) };
  }
}

/**
 * Initiate password reset
 */
export async function forgotPassword(username: string): Promise<AuthResult> {
  try {
    const input: ResetPasswordInput = { username };
    const result = await resetPassword(input);

    return {
      success: true,
      nextStep: result.nextStep?.resetPasswordStep,
    };
  } catch (error: any) {
    return { success: false, error: getAuthErrorMessage(error) };
  }
}

/**
 * Confirm password reset with verification code
 */
export async function confirmForgotPassword(
  username: string,
  code: string,
  newPassword: string
): Promise<AuthResult> {
  try {
    const input: ConfirmResetPasswordInput = {
      username,
      confirmationCode: code,
      newPassword,
    };

    await confirmResetPassword(input);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: getAuthErrorMessage(error) };
  }
}

/**
 * Change password for authenticated user
 */
export async function changePassword(
  oldPassword: string,
  newPassword: string
): Promise<AuthResult> {
  try {
    const input: UpdatePasswordInput = {
      oldPassword,
      newPassword,
    };

    await updatePassword(input);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: getAuthErrorMessage(error) };
  }
}

/**
 * Set up TOTP MFA for user
 */
export async function setupTOTP(): Promise<MFASetupResult> {
  const result = await setUpTOTP();

  const secretCode = result.sharedSecret;
  const qrCodeUri = result.getSetupUri('EASI').toString();

  return { secretCode, qrCodeUri };
}

/**
 * Verify TOTP setup with code
 */
export async function verifyTOTP(code: string): Promise<AuthResult> {
  try {
    await verifyTOTPSetup({ code });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: getAuthErrorMessage(error) };
  }
}

/**
 * Get the currently authenticated user
 */
export async function getAuthenticatedUser(): Promise<AuthUser | null> {
  try {
    const user = await getCurrentUser();
    const attributes = await fetchUserAttributes();

    return {
      userId: user.userId,
      username: user.username,
      email: attributes.email || '',
      emailVerified: attributes.email_verified === 'true',
      mfaEnabled: true, // Required for HIPAA
      roles: parseRoles(attributes['custom:roles']),
      organizationId: attributes['custom:organization_id'],
    };
  } catch (error) {
    return null;
  }
}

/**
 * Find the Cognito refresh token from any storage location.
 * Checks: (1) SSO manual key, (2) Amplify/Cognito dynamic key pattern.
 */
function getCognitoRefreshToken(): string | null {
  // 1. SSO OAuth stores it here
  const ssoRefresh = localStorage.getItem('easi_refresh_token');
  if (ssoRefresh) return ssoRefresh;

  // 2. Amplify/Cognito SDK stores it under a dynamic key:
  //    CognitoIdentityServiceProvider.<clientId>.<username>.refreshToken
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('CognitoIdentityServiceProvider.') && key.endsWith('.refreshToken')) {
      const value = localStorage.getItem(key);
      if (value) return value;
    }
  }

  return null;
}

/**
 * Refresh tokens using the Cognito token endpoint.
 * Returns new AuthSession on success, null on failure.
 */
async function refreshSSOTokens(refreshToken: string): Promise<AuthSession | null> {
  // Network errors (offline, DNS, timeout) THROW so the caller can
  // distinguish them from genuine token rejection and keep SSO tokens intact.
  const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.cognito.clientId,
      refresh_token: refreshToken,
    }),
  });
  // Cognito responded — parse the result
  try {
    const tokens = await response.json();
    if (tokens.id_token && tokens.access_token) {
      localStorage.setItem('easi_id_token', tokens.id_token);
      localStorage.setItem('easi_access_token', tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem('easi_refresh_token', tokens.refresh_token);
      }
      const payload = JSON.parse(atob(tokens.access_token.split('.')[1]));
      return {
        accessToken: tokens.access_token,
        idToken: tokens.id_token,
        expiresAt: new Date((payload.exp as number) * 1000 || Date.now() + 3600000),
      };
    }
  } catch {
    // JSON parse or token decode failed — treat as rejection
  }
  // Cognito explicitly rejected the refresh token (HTTP 400/401) — return null
  return null;
}

/**
 * Get current auth session with tokens.
 *
 * Checks localStorage first for SSO tokens, then falls back to Amplify.
 * If SSO tokens are expired, attempts silent refresh via Cognito token endpoint.
 *
 * @param forceRefresh - When true (e.g. after a 401), forces token refresh
 *   even if cached tokens appear valid. Passed through to Amplify's
 *   fetchAuthSession() and triggers manual refresh on the SSO path.
 */
export async function getAuthSession(forceRefresh?: boolean): Promise<AuthSession | null> {
  // Check for SSO tokens stored by OAuth callback (useAuth.tsx)
  const ssoAccess = localStorage.getItem('easi_access_token');
  const ssoId = localStorage.getItem('easi_id_token');
  if (ssoAccess && ssoId) {
    try {
      const payload = JSON.parse(atob(ssoAccess.split('.')[1]));
      const expiresAt = (payload.exp as number) * 1000;

      // Return cached token if still valid (60s buffer) and not forcing refresh
      if (!forceRefresh && expiresAt > Date.now() + 60_000) {
        return {
          accessToken: ssoAccess,
          idToken: ssoId,
          expiresAt: new Date(expiresAt),
        };
      }

      // Token expired (or force refresh requested) — try refresh
      const refreshToken = getCognitoRefreshToken();
      if (refreshToken) {
        try {
          const refreshed = await refreshSSOTokens(refreshToken);
          if (refreshed) return refreshed;
          // refreshSSOTokens returned null → Cognito explicitly rejected the
          // refresh token. Clear stale SSO tokens and fall through to Amplify.
          localStorage.removeItem('easi_id_token');
          localStorage.removeItem('easi_access_token');
          localStorage.removeItem('easi_refresh_token');
        } catch {
          // Network error during refresh — tokens may still be valid.
          // Do NOT clear them. Return the expired tokens so the caller
          // can retry later. This prevents session destruction on tab
          // return when the network is briefly unavailable.
          return {
            accessToken: ssoAccess,
            idToken: ssoId,
            expiresAt: new Date(expiresAt),
          };
        }
      } else {
        // No refresh token available — clear stale SSO tokens
        localStorage.removeItem('easi_id_token');
        localStorage.removeItem('easi_access_token');
      }
    } catch {
      // Token parsing failed — fall through to Amplify
    }
  }

  try {
    // Amplify handles token refresh internally when forceRefresh is true
    const session = await fetchAuthSession({ forceRefresh: forceRefresh || false });

    if (!session.tokens) {
      return null;
    }

    const { accessToken, idToken } = session.tokens;
    const idTokenStr = idToken?.toString() || '';
    const accessTokenStr = accessToken?.toString() || '';

    // After a forced refresh, persist to localStorage so the SSO fast-path
    // returns the new tokens immediately (avoids Amplify cache staleness)
    if (forceRefresh && idTokenStr) {
      localStorage.setItem('easi_id_token', idTokenStr);
      if (accessTokenStr) {
        localStorage.setItem('easi_access_token', accessTokenStr);
      }
    }

    return {
      accessToken: accessTokenStr,
      idToken: idTokenStr,
      expiresAt: new Date(
        (accessToken?.payload?.exp as number) * 1000 || Date.now() + 3600000
      ),
    };
  } catch (error) {
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}

/**
 * Get current access token (for API requests)
 *
 * Checks localStorage first for tokens stored by the SSO OAuth callback
 * (useAuth.tsx), then falls back to Amplify's session for email/password login.
 */
export async function getAccessToken(): Promise<string | null> {
  // SSO OAuth callback stores tokens in localStorage
  const ssoToken = localStorage.getItem('easi_access_token');
  if (ssoToken) {
    return ssoToken;
  }

  // Amplify-managed session (email/password sign-in)
  const session = await getAuthSession();
  return session?.accessToken || null;
}

/**
 * Get current ID token (contains user claims)
 */
export async function getIdToken(): Promise<string | null> {
  const session = await getAuthSession();
  return session?.idToken || null;
}

/**
 * Parse roles from custom attribute
 */
function parseRoles(rolesAttribute?: string): string[] {
  if (!rolesAttribute) {
    return [];
  }
  try {
    return JSON.parse(rolesAttribute);
  } catch {
    return rolesAttribute.split(',').map((r) => r.trim());
  }
}

/**
 * Get user-friendly error message
 */
function getAuthErrorMessage(error: any): string {
  const errorName = error.name || error.code || '';

  const errorMessages: Record<string, string> = {
    UserNotFoundException: 'User not found. Please check your username.',
    NotAuthorizedException: 'Incorrect username or password.',
    UserNotConfirmedException: 'Please verify your email before signing in.',
    UsernameExistsException: 'An account with this email already exists.',
    CodeMismatchException: 'Invalid verification code. Please try again.',
    ExpiredCodeException: 'Verification code has expired. Please request a new one.',
    LimitExceededException: 'Too many attempts. Please try again later.',
    InvalidPasswordException: 'Password needs at least 15 characters, with uppercase, lowercase, a number, and a symbol (like !@#$).', // pragma: allowlist secret
    InvalidParameterException: 'Invalid input. Please check your information.',
  };

  return errorMessages[errorName] || 'An error occurred. Please try again.';
}

export default {
  signIn: signInUser,
  signOut: signOutUser,
  signUp: signUpUser,
  confirmSignUp: confirmUserSignUp,
  forgotPassword,
  confirmForgotPassword,
  changePassword,
  setupTOTP,
  verifyTOTP,
  confirmMFA,
  getUser: getAuthenticatedUser,
  getSession: getAuthSession,
  getAccessToken,
  getIdToken,
  isAuthenticated,
};
